<template>
  <LoaderWrapper ref="loader" class="h-100">
    <div class="row m-4 mt-0 mb-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div class="row">
            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <DatePicker v-model="fromDate" mode="date" :model-modifiers="{string: true}" :masks="{modelValue: 'YYYY-MM-DD', input: 'YYYY-MM-DD'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.from_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>

            <div class="form-group mb-2 me-2 col-md-3 col-lg-3 col-xl-2">
              <div class="col p-0">
                <DatePicker v-model="toDate" mode="date" :model-modifiers="{string: true}" :masks="{modelValue: 'YYYY-MM-DD', input: 'YYYY-MM-DD'}">
                  <template v-slot="{ inputValue, togglePopover, inputEvents }">
                    <input
                      class="form-control form-control-sm"
                      :placeholder="helper.trans('labels.to_date')"
                      :value="inputValue"
                      @focus="togglePopover"
                      @change="inputEvents.change"
                    />
                  </template>
                </DatePicker>
              </div>
            </div>

            <div class="form-group mb-2 col">
              <div class="col-auto p-0 text-end">
                <button type="button" @click.prevent="load()" class="btn btn-outline-primary btn-sm rounded-pill me-2 text-capitalize-first">{{ helper.trans('button.apply') }}</button>
                <button type="button" @click.prevent="reset()" class="btn btn-outline-secondary btn-sm rounded-pill text-capitalize-first">{{ helper.trans('button.reset') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row m-4 mb-0 mt-0">
      <div class="col-sm-12">
        <div class="card card-body mb-0">
          <div v-if="loaded && items.length === 0" class="text-center text-muted p-4">
            {{ helper.trans('statistic.receiptChecks.empty') }}
          </div>
          <div v-else-if="items.length > 0" class="overflow-auto">
            <table class="table table-bordered align-middle text-left text-nowrap position-relative">
              <thead>
                <tr class="bg-light">
                  <th class="text-capitalize-first">{{ helper.trans('statistic.receiptChecks.column.trader') }}</th>
                  <th class="text-capitalize-first text-end">{{ helper.trans('statistic.receiptChecks.column.count') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in items" :key="item.traderId">
                  <td>
                    {{ item.traderName }}
                    <div><small class="text-muted">{{ item.traderId }}</small></div>
                  </td>
                  <td class="text-end">{{ numberFormatter.formatNumber(item.count) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </LoaderWrapper>
</template>

<script lang="ts">
import {defineComponent, onMounted, reactive, ref, toRefs} from 'vue'
import {DatePicker} from "v-calendar";
import {useNotification} from "@kyvg/vue3-notification";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import ReceiptCheckStatisticDTO from "@/models/ReceiptCheckStatisticDTO";
import APIClient from "@/service/APIClient";
import {helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";

const api = new APIClient();
const notification = useNotification();

const dateParam = (value: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  return value.substring(0, 10);
}

export default defineComponent({
  name: 'StatisticReceiptChecks',
  components: {
    LoaderWrapper,
    DatePicker,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const state = reactive({
      fromDate: null as string | null,
      toDate: null as string | null,
      items: [] as ReceiptCheckStatisticDTO[],
      loaded: false,
    });

    const load = async () => {
      loader.value?.show();
      try {
        state.items = await api.call.statistic.receiptChecks({
          fromDate: dateParam(state.fromDate),
          toDate: dateParam(state.toDate),
        });
        state.loaded = true;
      } catch (e) {
        notification.notify({type: 'error', title: helper.trans('server_errors.INTERNAL_ERROR')});
      } finally {
        loader.value?.hide();
      }
    };

    const reset = async () => {
      state.fromDate = null;
      state.toDate = null;
      await load();
    };

    onMounted(() => {
      load();
    });

    return {
      ...toRefs(state),
      helper,
      numberFormatter,
      loader,
      load,
      reset,
    }
  },
})
</script>
